import React from 'react';
import VerifiedFlame from '../VerifiedFlame';
import { displayName, pickStatStrip, coachCtaHref, playerCardPath } from '../../lib/publicProfile';

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const QrIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" /><line x1="14" y1="14" x2="21" y2="21" />
  </svg>
);

export default function Hero({ athlete, catalog, isCoach, onShare, copied, ctaRef }) {
  const a = athlete;
  const { first, last } = displayName(a);
  const initials = `${(first[0] || '')}${(last[0] || '')}`.toUpperCase();
  const positions = [a.position_primary, a.position_secondary, a.position_other]
    .filter(Boolean).join(' / ');
  const school = [a.school ?? a.high_school, (a.high_school_city && a.high_school_state)
    ? `${a.high_school_city}, ${a.high_school_state}` : null].filter(Boolean).join(' — ');
  const cells = pickStatStrip(a, catalog);
  const cardHref = playerCardPath(a);

  // A coach who is already signed in skips the login round-trip entirely.
  const primaryHref = coachCtaHref(a, isCoach);
  const primaryLabel = isCoach ? 'View Full Recruiting File' : 'See Full Profile';

  return (
    <header className="ed-pp__hero">
      <div className="ed-pp__clamp">
        <div className="ed-pp__topbar">
          <img src="/logo-wordmark-white.png" alt="ProZpects" className="ed-pp__wordmark" />
          <a className="ed-pp__ghost-btn" href={isCoach ? '/dashboard' : coachCtaHref(a, false)}>
            {isCoach ? 'Dashboard' : 'Coach Login'}
          </a>
        </div>

        <div className="ed-pp__identity">
          <div className="ed-pp__photo">
            {a.primary_photo
              ? <img src={a.primary_photo} alt={`${first} ${last}`} />
              : <span className="ed-pp__photo-monogram" aria-hidden="true">{initials}</span>}
          </div>

          <div className="ed-pp__identity-body">
            <div className="ed-pp__eyebrow">
              {/* ed-pp__eyebrow-page: Task 10's mobile rules hide this element
                  specifically, so the class has to land here regardless of
                  what else the eyebrow renders. */}
              <span className="ed-pp__eyebrow-page">Athlete Profile</span>
              {a.sport && <span className="ed-pp__eyebrow-sport">{a.sport}</span>}
              {a.graduation_year && <span>Class of {a.graduation_year}</span>}
            </div>

            <h1 className="ed-pp__name">
              {first}{' '}
              {last && <span className="ed-pp__name-last">{last}</span>}
            </h1>

            {(positions || school) && (
              <p className="ed-pp__sub">
                {positions && <strong>{positions}</strong>}
                {/* Editorial.css's .ed-pp__sub-dot dims this separator to 0.3
                    opacity, distinct from the 0.62 of the surrounding text —
                    a plain " · " string can't carry that, so it needs the
                    class Task 5 built for exactly this separator. */}
                {positions && school && <span className="ed-pp__sub-dot"> · </span>}
                {/* The school crest was on the old public header and the spec
                    says nothing currently public is removed. The Editorial
                    hero has no dedicated slot for it, so it rides inline
                    immediately before the school name it belongs to — and is
                    decorative, since the name is right there in text. */}
                {school && a.school_logo && (
                  <img src={a.school_logo} alt="" aria-hidden="true" className="ed-pp__school-logo" />
                )}
                {school}
              </p>
            )}

            <div className="ed-pp__actions">
              <a ref={ctaRef} className="ed-pp__btn ed-pp__btn--primary" href={primaryHref}>
                {primaryLabel} <ArrowRight />
              </a>
              <button type="button" className="ed-pp__btn ed-pp__btn--ghost" onClick={onShare}>
                <ShareIcon /> {copied ? 'Copied!' : 'Share'}
              </button>
              <a className="ed-pp__btn ed-pp__btn--ghost" href={cardHref}>
                <QrIcon /> Player Card
              </a>
            </div>

            {!isCoach && (
              <p className="ed-pp__actions-caption">
                Full detail requires a coach login — free for verified college staff
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="ed-pp__strip" role="list">
        {cells.map(c => (
          <div key={c.key} className="ed-pp__stat" role="listitem">
            <span className={`ed-pp__stat-value${c.value === null ? ' ed-pp__stat-value--empty' : ''}`}>
              {c.value === null ? '—' : c.value}
              {c.verified && <VerifiedFlame size={15} />}
            </span>
            <span className="ed-pp__stat-label">{c.label}</span>
          </div>
        ))}
      </div>
    </header>
  );
}
